import { Routes, Route, useLocation, useParams, Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import CalendarScreen from './CalendarScreen';
import SchedulerScreen from './SchedulersScreen';
import { GET_SCHEDULE, LIST_CALENDARS_RULER } from '~/services/calendar';
import { publicClient } from '~/services';
import ScheduleConfirmationScreen from './ScheduleConfirmationScreen';
import { useState, useEffect } from 'react';
import CreateNewSchedule from './CreateNewSchedule';

const CalendarRouter = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { schedule_id } = useParams();
    
    // Check if this is a preview route and get agentId from query params
    const isPreviewMode = schedule_id === 'preview';
    const urlParams = new URLSearchParams(location.search);
    const agentId = urlParams.get('agentId');
    
    const { data: scheduleData, loading: scheduleLoading } = useQuery(GET_SCHEDULE, {
        variables: {
            data: {
                scheduler_id: schedule_id,
            },
        },
        client: publicClient,
        skip: isPreviewMode, // Skip query if in preview mode
    });

    // For preview mode, get calendars using agentId directly
    const { data: calendarsData, loading: calendarsLoading } = useQuery(LIST_CALENDARS_RULER, {
        variables: {
            data: {
                agent_id: isPreviewMode ? agentId : scheduleData?.get_schedule?.data?.agent_id,
            },
        },
        client: publicClient,
        skip: (!scheduleData && !isPreviewMode) || (isPreviewMode && !agentId), // Skip if no data and not preview, or if preview but no agentId
    });

    const [hasRedirected, setHasRedirected] = useState(false);

    useEffect(() => {
        if (!isPreviewMode && scheduleData?.get_schedule?.data && calendarsData?.list_calendar_ruler?.data && !hasRedirected) {
            const scheduleInfo = scheduleData.get_schedule.data;
            
            // Don't redirect if the appointment is rejected/canceled
            if (scheduleInfo.status === 'REJECTED') {
                return;
            }
            
            // Redirect if we have a start_date (appointment already scheduled) OR calendar_id (calendar already selected)
            if (scheduleInfo.start_date || scheduleInfo.calendar_id) {
                const calendar = calendarsData.list_calendar_ruler.data.find(
                    (calendarRuler: any) => calendarRuler._id === (scheduleInfo.calendar_rulers_id || scheduleInfo.calendar_id)
                );
                setHasRedirected(true);
                navigate(`/calendar/${schedule_id}/scheduler`, { state: { selectedCalendars: [calendar] } });
            }
        }
    }, [scheduleData, location.pathname, schedule_id, calendarsData, hasRedirected, isPreviewMode]);

    // Handle preview mode
    if (isPreviewMode) {
        if (!agentId) {
            return (
                <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
                    <div className="alert alert-brand-error">
                        <p className="text-brand-body">
                            Error: Se requiere agentId para la vista previa.
                        </p>
                    </div>
                </div>
            );
        }

        if (calendarsLoading) {
            return (
                <div className="flex items-center justify-center min-h-screen">
                    <div className="skeleton-brand w-full max-w-md h-48 rounded-lg"></div>
                </div>
            );
        }

        return (
            <Routes>
                <Route path="/" element={<CalendarScreen scheduleData={null} calendarsData={calendarsData?.list_calendar_ruler?.data} />} />
                <Route path="/scheduler" element={
                    <SchedulerScreen 
                        scheduleData={null} 
                        isPreviewMode={true} 
                        agentId={agentId}
                        calendarsData={calendarsData?.list_calendar_ruler?.data || []}
                    />
                } />
            </Routes>
        );
    }

    if (scheduleLoading || calendarsLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="skeleton-brand w-full max-w-md h-48 rounded-lg"></div>
            </div>
        );
    }

    if (!scheduleData.get_schedule.data || !calendarsData) {
        return (
            <div className="mx-auto mt-8 flex h-full w-full max-w-[1024px] flex-col items-center justify-center">
                <div className="alert alert-brand-error">
                    <p className="text-brand-body">
                        Error: No se pudo cargar la información del calendario.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            <Route path="/" element={<CalendarScreen scheduleData={scheduleData.get_schedule?.data} calendarsData={calendarsData?.list_calendar_ruler?.data} />} />
            <Route path="/scheduler" element={<SchedulerScreen scheduleData={scheduleData.get_schedule?.data} />} />
            <Route path="/schedule-confirmation" element={<ScheduleConfirmationScreen  />} />
            <Route path="/create_new_schedule" element={<CreateNewSchedule />} />
             {/* Add more routes here if needed */}
        </Routes>
    );
};

export default CalendarRouter;
